import React from "react";
import clsx from "clsx";
import { isFalsy, isTruthy } from "@/utils/guards";

import { Button } from "@proda-ai/ui-kit";

import Box from "@/ui/atoms/Box";
import Loader from "@/ui/atoms/Loader";
import ErrorMessage from "@/ui/atoms/ErrorMessage";

import type { AgGridReact, GridApi } from "@/ui/molecules/Grid";

import type { GridReviewProps } from "@/ui/organisms/GridReview";
import GridReview from "@/ui/organisms/GridReview";

import type { ExtractionType } from "@/ui/organisms/dealTypes";

import type { LayoutFullScreenWithCollapsibleSidebarProps } from "@/ui/layouts/LayoutFullScreenWithCollapsibleSidebar";
import LayoutFullScreenWithCollapsibleSidebar from "@/ui/layouts/LayoutFullScreenWithCollapsibleSidebar";
import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";
import LayoutFullScreenOverlay from "@/ui/layouts/LayoutFullScreenOverlay";
import { CloseExtractionModal } from "@/ui/organisms/CloseExtractionModal";

import type { ExtractionsErrorListProps } from "@/ui/organisms/ExtractionsSidebar/ExtractionsErrorList";
import { ExtractionConversionModal } from "@/ui/organisms/ExtractonConversionModal";
import { ExtractionCompleteConfirmationModal } from "@/ui/organisms/ExtractionCompleteConfirmationModal";
import ABTLCollapsible, {
    testId as ABTLCollapsibleTestId,
} from "@/ui/organisms/ABTLCollapsible";
import { formatNumber } from "@/ui/organisms/utils";
import PillDropdown from "@/ui/molecules/PillDropdown";
import type { PillDropdownOption } from "@/ui/molecules/PillDropdown";

const getTypedEmptyArray = <T,>() => [] as T[];

export const sidebarInfo = {
    draft: {
        header: "2/2 Review data",
        description:
            "Review data outliers and anomalies of the standardized document",
    },
    completed: {
        header: "View and Export",
        description: "View the standardized extraction and export it",
    },
};

export type ViewExtractionReviewParams = {
    useState: (params: {
        extractionId: number;
        projectId?: number;
        databaseId?: number;
    }) => {
        loadStandardizedExtractionStatus:
            | "idle"
            | "pending"
            | "resolved"
            | "rejected";
        columnDefsOperatingStatements:
            | GridReviewProps["columnDefsOperatingStatements"]
            | null;
        extractionStatus: "draft" | "completed" | null;
        reviewData: GridReviewProps["reviewData"] | null;
        reviewDataBelowTheLine: GridReviewProps["reviewData"] | null;
        dealName: string | null;
        standardizedTotalNetIncomeOriginal: { [key: string]: number } | null;
        standardizedOriginalNetIncomeTotalName: string | null;
        standardizedTotalNetIncomeAltered: { [key: string]: number } | null;
        errorGroups: ExtractionsErrorListProps["errors"];
        convertToDraftModalOpen: boolean;
        saveExtractionCompleteModalOpen: boolean;
        closeExtractionModalOpen: boolean;
        activeColumns: GridReviewProps["activeColumns"] | null;
        extractionsInfo:
            | {
                  fileName: string;
                  extractionId: number;
                  sheetName: string;
              }[]
            | null;
        conflictingExtractions: number[] | null;
        extractionType: ExtractionType | null;
        isCompletionDataPending?: boolean;
    };
    useCases: () => {
        showLoadingOverlay: boolean;
        onMount: ({
            projectId,
            databaseId,
            extractionId,
        }: {
            projectId: number;
            databaseId: number;
            extractionId: number;
        }) => void;
        onUnmount: () => void;
        onCloseRequested: (params: {
            projectId: number;
            databaseId: number;
            extractionId: number;
            extractionStatus: "draft" | "completed" | null;
        }) => void;
        onBackClicked: ({
            projectId,
            databaseId,
            extractionId,
        }: {
            projectId: number;
            databaseId: number;
            extractionId: number;
        }) => void;
        onSaveExtractionClicked: (params: {
            projectId: number;
            databaseId: number;
            extractionId: number;
        }) => Promise<void>;
        onSaveDraftClicked: (params: {
            projectId: number;
            databaseId: number;
            extractionId: number;
        }) => Promise<void>;
        onConvertToDraftClicked: (params: {
            projectId: number;
            databaseId: number;
            extractionId: number;
            extractionIds: number[];
        }) => void;
        onExportExtractionClicked: ({
            extractionId,
            extractionType,
            projectId,
            databaseId,
        }: {
            extractionId: number;
            extractionType: ExtractionType;
            projectId: number;
            databaseId: number;
        }) => void;
        onDiscardExtraction: (params: {
            projectId: number;
            databaseId: number;
            extractionId: number;
        }) => void;
        setConvertToDraftModalOpenState: (state: boolean) => void;
        setSaveExtractionCompleteModalOpenState: (state: boolean) => void;
        setCloseExtractionModalOpenState: (state: boolean) => void;
        setActiveColumn: (label: string, extractionId: number) => void;
        checkConflictingExtractionsForConversionToDraftAndOpenModal: (params: {
            projectId: number;
            databaseId: number;
            extractionId: number;
        }) => void;
        onUpdateExtractionType: (params: {
            extractionId: number;
            extractionType: ExtractionType;
        }) => void;
    };
    ComponentSidebar: React.FC<{
        extractionId: number;
        projectId?: number;
        databaseId?: number;
        gridApi: GridApi | null;
        description: string;
        allFoundTables: null;
        onTableChange: () => void;
        isFromLlm: boolean;
    }>;
    ComponentConcurrentUsersIndicator: React.FC;
    ComponentConcurrentUsersBanner: React.FC;
    ComponentConcurrentUsersStartSessionOnMountAndStopOnUnmount: React.FC<{
        extractionId: number;
        resourceKey: "extraction-mapping";
    }>;
};

export type ViewExtractionReviewProps = Omit<
    Omit<
        LayoutFullScreenWithCollapsibleSidebarProps,
        | "headerText"
        | "sidebarHeader"
        | "sidebarContent"
        | "isSidebarCollapsed"
        | "onSidebarToggle"
        | "onClose"
        | "footerContent"
    >,
    "children"
> & {
    projectId: number;
    databaseId: number;
    extractionId: number;
};

export const testIds = {
    main: "ViewExtractionReview",
    belowTheLineGrid: "ViewExtractionReview-below-the-line-grid",
    buttonBack: "ViewExtractionReview-button-back",
    buttonConvertToDraft: "ViewExtractionReview-button-convert-to-draft",
    buttonExport: "ViewExtractionReview-button-export",
    buttonSaveAsDraft: "ViewExtractionReview-button-save-as-draft",
    buttonSaveAsCompleted: "ViewExtractionReview-button-save-as-completed",
    belowTheLineGridToggle: ABTLCollapsibleTestId,
};

export const createViewExtractionReview = ({
    useState,
    useCases,
    ComponentSidebar = () => null,
    ComponentConcurrentUsersIndicator = () => null,
    ComponentConcurrentUsersBanner = () => null,
    ComponentConcurrentUsersStartSessionOnMountAndStopOnUnmount = () => null,
}: ViewExtractionReviewParams) => {
    const ViewExtractionReview: React.FC<ViewExtractionReviewProps> = props => {
        const {
            className,
            projectId,
            databaseId,
            extractionId,
            ...otherProps
        } = props;

        const [isSidebarCollapsed, setIsSidebarCollapsed] =
            React.useState(false);
        const [gridApi, setGridApi] = React.useState<GridApi | null>(null);

        const operatingStatementsGridRef = React.useRef<AgGridReact>(null);
        const belowTheLineGridRef = React.useRef<AgGridReact>(null);

        const {
            onMount,
            onUnmount,
            onCloseRequested,
            onBackClicked,
            onSaveExtractionClicked,
            onSaveDraftClicked,
            onConvertToDraftClicked,
            onExportExtractionClicked,
            onDiscardExtraction,
            setConvertToDraftModalOpenState,
            setSaveExtractionCompleteModalOpenState,
            setCloseExtractionModalOpenState,
            setActiveColumn,
            checkConflictingExtractionsForConversionToDraftAndOpenModal,
            onUpdateExtractionType,
            showLoadingOverlay,
        } = useCases();

        const {
            loadStandardizedExtractionStatus,
            extractionStatus,
            columnDefsOperatingStatements,
            reviewData,
            dealName,
            standardizedTotalNetIncomeOriginal,
            standardizedOriginalNetIncomeTotalName,
            standardizedTotalNetIncomeAltered,
            errorGroups,
            convertToDraftModalOpen,
            saveExtractionCompleteModalOpen,
            closeExtractionModalOpen,
            activeColumns,
            extractionsInfo,
            conflictingExtractions,
            reviewDataBelowTheLine,
            extractionType,
            isCompletionDataPending = false,
        } = useState({ extractionId, projectId, databaseId });

        const isDraft = extractionStatus === "draft";
        const isCompleted = extractionStatus === "completed";
        const isYearToDate = extractionType === "year_to_date";
        const showBelowTheLineGrid =
            reviewDataBelowTheLine !== null &&
            reviewDataBelowTheLine.length > 0;

        const pageTitle = `${isTruthy(dealName) ? `${dealName} – ` : ""}Operating Statement`;
        const sidebarHeader =
            extractionStatus === null
                ? sidebarInfo.draft.header
                : isCompleted
                  ? sidebarInfo.completed.header
                  : sidebarInfo.draft.header;

        const sidebarDescription =
            extractionStatus === null
                ? sidebarInfo.draft.description
                : isCompleted
                  ? sidebarInfo.completed.description
                  : sidebarInfo.draft.description;

        const yearlyViewModeOptions = React.useMemo<
            PillDropdownOption<ExtractionType>[]
        >(
            () => [
                {
                    label: "Budget",
                    value: "operating_statement_yearly_budget",
                    icon: "chart-mixed-up-circle-dollar",
                },
                {
                    label: "Actual",
                    value: "operating_statement_yearly_actual",
                    icon: "file-spreadsheet",
                },
                {
                    label: "Actual to Budget",
                    value: "operating_statement_actual_to_budget",
                    icon: "chart-mixed-up-circle-dollar",
                    disabled: true,
                    hidden: true,
                },
            ],
            [],
        );

        const monthlyViewModeOptions = React.useMemo<
            PillDropdownOption<ExtractionType>[]
        >(
            () => [
                {
                    label: "Budget",
                    value: "operating_statement_monthly_budget",
                    icon: "chart-mixed-up-circle-dollar",
                },
                {
                    label: "Trailing",
                    value: "operating_statement_monthly_actual",
                    icon: "file-spreadsheet",
                },
                {
                    label: "Actual to Budget",
                    value: "operating_statement_actual_to_budget",
                    icon: "chart-mixed-up-circle-dollar",
                    disabled: true,
                    hidden: true,
                },
            ],
            [],
        );

        const viewModeOptions = React.useMemo(() => {
            if (isFalsy(extractionType)) {
                return [];
            }
            if (extractionType.includes("yearly")) {
                return yearlyViewModeOptions;
            }
            return monthlyViewModeOptions;
        }, [extractionType, yearlyViewModeOptions, monthlyViewModeOptions]);

        React.useEffect(() => {
            void onMount({ projectId, databaseId, extractionId });

            return onUnmount;
        }, [projectId, databaseId, extractionId, onMount, onUnmount]);

        const onGridReviewReadyHandler = React.useCallback<
            Exclude<GridReviewProps["onGridReady"], undefined>
        >(({ api }) => {
            setGridApi(api);
        }, []);

        const activeColumnsChanges = React.useMemo(
            () =>
                Object.entries(activeColumns ?? {}).filter(
                    ([, change]) => change.from !== change.to,
                ),
            [activeColumns],
        );

        const longestOperatingStatementAndTotalsNumericValue = React.useMemo(
            () =>
                (reviewData ?? []).reduce((acc, row) => {
                    const isNumberTypeGuard = (
                        value: unknown,
                    ): value is number => typeof value === "number";
                    const numericValues =
                        Object.values(row).filter(isNumberTypeGuard);
                    const numericValuesFormattedLengths = numericValues.map(
                        value => formatNumber(value).length,
                    );
                    const longestValue = Math.max(
                        ...numericValuesFormattedLengths,
                    );
                    return Math.max(acc, longestValue);
                }, 0),
            [reviewData],
        );

        if (loadStandardizedExtractionStatus === "rejected") {
            return (
                <LayoutFullScreenWithCollapsibleSidebar
                    headerText={pageTitle}
                    sidebarHeader={sidebarHeader}
                    onClose={() =>
                        onCloseRequested({
                            projectId,
                            databaseId,
                            extractionId,
                            extractionStatus,
                        })
                    }
                >
                    <LayoutFillParentCentered>
                        <ErrorMessage>
                            There was an error loading the standardized
                            extraction.
                        </ErrorMessage>
                    </LayoutFillParentCentered>
                </LayoutFullScreenWithCollapsibleSidebar>
            );
        }

        if (
            loadStandardizedExtractionStatus === "pending" ||
            loadStandardizedExtractionStatus === "idle"
        ) {
            return (
                <LayoutFullScreenWithCollapsibleSidebar
                    headerText={pageTitle}
                    sidebarHeader={sidebarHeader}
                    onClose={() =>
                        onCloseRequested({
                            projectId,
                            databaseId,
                            extractionId,
                            extractionStatus,
                        })
                    }
                >
                    <LayoutFillParentCentered>
                        <Loader />
                    </LayoutFillParentCentered>
                </LayoutFullScreenWithCollapsibleSidebar>
            );
        }

        return (
            <>
                <ComponentConcurrentUsersStartSessionOnMountAndStopOnUnmount
                    extractionId={extractionId}
                    resourceKey={"extraction-mapping"}
                />
                {showLoadingOverlay && (
                    <LayoutFullScreenOverlay>
                        <LayoutFillParentCentered>
                            <Loader />
                        </LayoutFillParentCentered>
                    </LayoutFullScreenOverlay>
                )}
                <ExtractionConversionModal
                    showModal={convertToDraftModalOpen}
                    conflictingExtractions={conflictingExtractions ?? []}
                    extractionsInfo={extractionsInfo}
                    onConfirm={() =>
                        void onConvertToDraftClicked({
                            projectId,
                            extractionId,
                            databaseId,
                            extractionIds: conflictingExtractions ?? [],
                        })
                    }
                    onCancel={() => setConvertToDraftModalOpenState(false)}
                />
                <ExtractionCompleteConfirmationModal
                    showModal={saveExtractionCompleteModalOpen}
                    onConfirm={() =>
                        void onSaveExtractionClicked({
                            projectId,
                            extractionId,
                            databaseId,
                        })
                    }
                    changes={activeColumnsChanges}
                    extractionsInfo={extractionsInfo}
                    onCancel={() =>
                        setSaveExtractionCompleteModalOpenState(false)
                    }
                />
                <CloseExtractionModal
                    showModal={closeExtractionModalOpen}
                    onConfirm={() =>
                        void onCloseRequested({
                            projectId,
                            databaseId,
                            extractionId,
                            extractionStatus: "draft",
                        })
                    }
                    onCancel={() => setCloseExtractionModalOpenState(false)}
                    onDiscard={() =>
                        void onDiscardExtraction({
                            projectId,
                            databaseId,
                            extractionId,
                        })
                    }
                />
                <LayoutFullScreenWithCollapsibleSidebar
                    className={clsx(testIds.main, className)}
                    data-testid={testIds.main}
                    {...otherProps}
                    headerText={pageTitle}
                    headerLeftMenu={
                        isDraft &&
                        !isYearToDate && (
                            <PillDropdown
                                options={viewModeOptions}
                                value={extractionType}
                                disabled={
                                    extractionType ===
                                    "operating_statement_actual_to_budget"
                                }
                                onChange={_extractionType => {
                                    onUpdateExtractionType({
                                        extractionId,
                                        extractionType: _extractionType,
                                    });
                                }}
                                colour="proda-purple"
                            />
                        )
                    }
                    sidebarHeader={sidebarHeader}
                    topbarRightContent={
                        isDraft && <ComponentConcurrentUsersIndicator />
                    }
                    sidebarContent={
                        <ComponentSidebar
                            extractionId={extractionId}
                            projectId={projectId}
                            databaseId={databaseId}
                            gridApi={gridApi}
                            description={sidebarDescription}
                            allFoundTables={null}
                            onTableChange={() => {}}
                            isFromLlm={false}
                        />
                    }
                    isSidebarCollapsed={isSidebarCollapsed}
                    onSidebarToggle={() =>
                        setIsSidebarCollapsed(!isSidebarCollapsed)
                    }
                    onClose={() =>
                        isCompleted
                            ? onCloseRequested({
                                  projectId,
                                  databaseId,
                                  extractionId,
                                  extractionStatus,
                              })
                            : setCloseExtractionModalOpenState(true)
                    }
                    footerContent={
                        isDraft ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    width: "100%",
                                    height: "100%",
                                    alignItems: "center",
                                    padding: "16px 80px 16px 16px",
                                }}
                            >
                                <Button
                                    data-testid={testIds.buttonBack}
                                    pattern="secondary"
                                    isGhost={true}
                                    isInline={true}
                                    size={"large"}
                                    leadingIcon={"chevron-left"}
                                    onClick={() =>
                                        void onBackClicked({
                                            projectId,
                                            databaseId,
                                            extractionId,
                                        })
                                    }
                                >
                                    Back to mapping
                                </Button>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        gap: "16px",
                                    }}
                                >
                                    <Button
                                        data-testid={testIds.buttonSaveAsDraft}
                                        pattern="secondary"
                                        size={"medium"}
                                        onClick={() =>
                                            void onSaveDraftClicked({
                                                projectId,
                                                extractionId,
                                                databaseId,
                                            })
                                        }
                                    >
                                        Save as Draft
                                    </Button>
                                    <Button
                                        data-testid={
                                            testIds.buttonSaveAsCompleted
                                        }
                                        pattern="primary"
                                        size={"medium"}
                                        onClick={() => {
                                            if (
                                                activeColumnsChanges.length > 0
                                            ) {
                                                setSaveExtractionCompleteModalOpenState(
                                                    true,
                                                );
                                            } else {
                                                void onSaveExtractionClicked({
                                                    projectId,
                                                    extractionId,
                                                    databaseId,
                                                });
                                            }
                                        }}
                                        disabled={
                                            Object.values(errorGroups).some(
                                                group =>
                                                    group.some(
                                                        error =>
                                                            error.severity ===
                                                                "error" &&
                                                            error.resolved !==
                                                                true,
                                                    ),
                                            ) || isCompletionDataPending
                                        }
                                    >
                                        Save as Completed
                                    </Button>
                                </Box>
                            </Box>
                        ) : isCompleted ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    padding: "16px 80px 16px 16px",
                                    gap: "16px",
                                }}
                            >
                                <Button
                                    data-testid={testIds.buttonConvertToDraft}
                                    pattern="danger"
                                    size={"medium"}
                                    onClick={() =>
                                        void checkConflictingExtractionsForConversionToDraftAndOpenModal(
                                            {
                                                projectId,
                                                databaseId,
                                                extractionId,
                                            },
                                        )
                                    }
                                >
                                    Convert to Draft
                                </Button>
                                <Button
                                    data-testid={testIds.buttonExport}
                                    pattern="primary"
                                    size={"medium"}
                                    onClick={() => {
                                        if (isTruthy(extractionType)) {
                                            void onExportExtractionClicked({
                                                extractionId,
                                                extractionType,
                                                projectId,
                                                databaseId,
                                            });
                                        }
                                    }}
                                >
                                    Export
                                </Button>
                            </Box>
                        ) : null
                    }
                >
                    <LayoutFillParentCentered
                        sx={{ display: "flex", flexDirection: "column" }}
                    >
                        {isDraft && <ComponentConcurrentUsersBanner />}
                        <Box sx={{ height: "100%", width: "100%" }}>
                            <GridReview
                                ref={operatingStatementsGridRef}
                                longestOperatingStatementAndTotalsNumericValue={
                                    longestOperatingStatementAndTotalsNumericValue
                                }
                                alignedGrids={[belowTheLineGridRef]}
                                isExtractionCompleted={isCompleted}
                                errorGroups={isDraft ? errorGroups : {}}
                                allowEditing={false}
                                extractionId={extractionId}
                                columnDefsOperatingStatements={
                                    columnDefsOperatingStatements ||
                                    getTypedEmptyArray()
                                }
                                reviewData={reviewData || getTypedEmptyArray()}
                                standardizedTotalNetIncomeOriginal={
                                    standardizedTotalNetIncomeOriginal
                                }
                                standardizedOriginalNetIncomeTotalName={
                                    standardizedOriginalNetIncomeTotalName
                                }
                                standardizedTotalNetIncomeAltered={
                                    standardizedTotalNetIncomeAltered
                                }
                                activeColumns={activeColumns}
                                extractionsInfo={extractionsInfo}
                                alwaysShowVerticalScroll={true}
                                setActiveColumn={setActiveColumn}
                                onGridReady={onGridReviewReadyHandler}
                            />
                        </Box>
                        {showBelowTheLineGrid && (
                            <ABTLCollapsible>
                                <GridReview
                                    ref={belowTheLineGridRef}
                                    data-testid={testIds.belowTheLineGrid}
                                    className={testIds.belowTheLineGrid}
                                    sx={{
                                        "& .ag-header": {
                                            display: "none",
                                        },
                                    }}
                                    longestOperatingStatementAndTotalsNumericValue={
                                        longestOperatingStatementAndTotalsNumericValue
                                    }
                                    alignedGrids={[operatingStatementsGridRef]}
                                    isExtractionCompleted={isCompleted}
                                    errorGroups={{}}
                                    allowEditing={false}
                                    extractionId={extractionId}
                                    columnDefsOperatingStatements={
                                        columnDefsOperatingStatements ||
                                        getTypedEmptyArray()
                                    }
                                    reviewData={reviewDataBelowTheLine}
                                    standardizedTotalNetIncomeOriginal={null}
                                    standardizedOriginalNetIncomeTotalName={
                                        null
                                    }
                                    standardizedTotalNetIncomeAltered={null}
                                    activeColumns={activeColumns}
                                    extractionsInfo={null}
                                    headerHeight={0}
                                    alwaysShowVerticalScroll={true}
                                    suppressCellFocus={true}
                                    setActiveColumn={() => {}}
                                    onGridReady={() => {}}
                                />
                            </ABTLCollapsible>
                        )}
                    </LayoutFillParentCentered>
                </LayoutFullScreenWithCollapsibleSidebar>
            </>
        );
    };

    return ViewExtractionReview;
};
